import { useEffect, useRef, useState } from "react";

export const useMultiTabAuth = ({
  onSessionReceived,
  onLogoutReceived,
  onTokensUpdated,
}) => {
  const channelRef = useRef(null);
  const [isLeader, setIsLeader] = useState(false);

  useEffect(() => {
    if (!("BroadcastChannel" in window)) return;

    const channel = new BroadcastChannel("auth");
    channelRef.current = channel;

    const tabId = Math.random().toString(36).substring(2);
    let leaderTimeout;

    // message handler
    channel.onmessage = (event) => {
      const { type, session } = event.data;

      switch (type) {
        case "REQUEST_SESSION":
          if (sessionStorage.getItem("authToken")) {
            channel.postMessage({
              type: "SHARE_SESSION",
              session: {
                authToken: sessionStorage.getItem("authToken"),
                refreshToken: sessionStorage.getItem("refreshToken"),
                accessTokenExpiresAt: sessionStorage.getItem(
                  "accessTokenExpiresAt",
                ),
                refreshTokenExpiresAt: sessionStorage.getItem(
                  "refreshTokenExpiresAt",
                ),
                userPrivileges: sessionStorage.getItem("userPrivileges"),
              },
            });
          }
          break;

        case "SHARE_SESSION":
          Object.entries(session || {}).forEach(([key, value]) => {
            if (value) sessionStorage.setItem(key, value);
          });

          onSessionReceived?.();
          break;

        case "TOKENS_UPDATED":
          Object.entries(session || {}).forEach(([key, value]) => {
            if (value) sessionStorage.setItem(key, value);
          });

          onTokensUpdated?.(session);
          break;

        case "LOGOUT":
          onLogoutReceived?.();
          break;

        case "TAB_OPEN":
          if (isLeader) {
            channel.postMessage({ type: "LEADER_EXISTS" });
          }
          break;

        case "LEADER_EXISTS":
          clearTimeout(leaderTimeout);
          setIsLeader(false);
          break;

        default:
          break;
      }
    };

    // leader election
    channel.postMessage({ type: "TAB_OPEN", tabId });

    leaderTimeout = setTimeout(() => {
      setIsLeader(true);
    }, 100);

    return () => {
      channel.close();
    };
  }, []);

  // methods
  const requestSession = () => {
    return new Promise((resolve) => {
      if (!channelRef.current) return resolve();

      if (sessionStorage.getItem("authToken")) return resolve();

      const timeout = setTimeout(resolve, 300);

      const handler = (event) => {
        if (event.data.type === "SHARE_SESSION") {
          clearTimeout(timeout);
          resolve();
        }
      };

      channelRef.current.addEventListener("message", handler, { once: true });
      channelRef.current.postMessage({ type: "REQUEST_SESSION" });
    });
  };

  const broadcastLogout = () => {
    channelRef.current?.postMessage({ type: "LOGOUT" });
  };

  const broadcastTokenUpdate = (session) => {
    channelRef.current?.postMessage({
      type: "TOKENS_UPDATED",
      session,
    });
  };

  return {
    isLeader,
    requestSession,
    broadcastLogout,
    broadcastTokenUpdate,
  };
};
